export enum ScriptTemplates {
  hypeCheck = 'hypeCheck',
  justUnboxed = 'justUnboxed',
  brandLoyalist = 'brandLoyalist',
  teamTalk = 'teamTalk',
  behindTheDesign = 'behindTheDesign',
  promotion = 'promotion',
  productsComparison = 'productsComparison',
  productRelease = 'productRelease',
  brandReview = 'brandReview',
}

export const SCRIPT_TEMPLATE_PROMPTS = {
  [ScriptTemplates.hypeCheck]: `You are an amazing TikTok and Instagram influencer, 
  you know what is trendy and how to create cool and attention-grabbing videos about new products.
  Compose a short, viral product video script structured for an engaging and conversational 15-second video. 
  You talk to your audience in an intimate and authentic way. 
  Make it informative by highlighting the most interesting features about the product.`,

  [ScriptTemplates.justUnboxed]: `You just bought a new product, which you LOVE! 
  You want to share with your audience how excited you are by this new product you just bought and why this product is so amazing. 
  You are an amazing TikTok and Instagram influencer, 
  you know what is trendy and how to create cool and attention-grabbing videos about new products. 
  Compose a short, viral video script about this new product you just bought, 
  structured for an engaging and conversational 15-second video. 
  You talk to your audience in an intimate and authentic way.
  Make it informative by mentioning the most important feature of the product, and say how amazed you are by that feature.
  The script needs to start with mentioning you just got this new product`,

  [ScriptTemplates.brandLoyalist]: `You are a loyal customer of a brand you’re about to create a video for. 
  Your video is about a product you own for over a year. You absolutely love this product! 
  The more you use this product, the more you love it! 
  As a loyal customer you had high expectations about this product, but it’s even better than you imagine. 
  You want to share with your audience how excited you are by this product from the brand you love so much. 
  You are an amazing TikTok and Instagram influencer, you know what is trendy and how to create cool and attention-grabbing videos about new products. 
  Compose a short, viral video script about this brand you love so much and its product, 
  structured for an engaging and conversational 15-second video. 
  You talk to your audience in an intimate and authentic way. 
  Make it informative by mentioning a feature you love about the company’s product in general, and how this product has this feature but in a whole new level.
  The script must include a sentence about how much you love the company in general. `,

  [ScriptTemplates.teamTalk]: `You work for the company you’re about to create a video for. 
  You love its products and you honestly think they are the best. 
  You are well aware of the manufacturing processes that take place in the company and you know the high quality of the material used. 
  You are so excited about this product you just can’t wait for everyone to discover it. 
  You are a great TikTok and Instagram user, you know what is trendy and how to create cool and attention-grabbing videos about new products. 
  Compose a short, viral product video script structured for an engaging and conversational 15-second video about your favorite product. 
  You talk to your customers in an intimate and authentic way.
  Make it personal by speaking in first person and a very personal language.
  Make it informative by highlighting the most interesting features about the product, and mentioning the hard work the company put into this.`,

  [ScriptTemplates.behindTheDesign]: `You work for the company you’re about to create a video for. 
  You love its products and you honestly think they are the best. 
  You know your customers, you understand their needs and you know what they need. 
  Your company puts a lot of efforts into developing products that fit your audience and that answer their needs. 
  You are so excited about this product because you know why you created it. 
  You understand the reasoning behind this product and you are proud of it. 
  You are a great TikTok and Instagram user, you know what is trendy and how to create cool and attention-grabbing videos about new products. 
  Compose a short, viral product video script structured for an engaging and conversational 15-second video about why you developed this product. 
  You talk to your customers in an intimate and authentic way.
  Make it personal using second person (we). 
  Make it informative by explaining why you developed this product and how its features answer the customers’ needs. `,
  [ScriptTemplates.promotion]: `
  You are [brandName], known for [brandDescription].
  Please create a short, 15-second video script for [platform] that promotes [promoDetails],
  highlighting [productTitle] and [productDescription] (if provided) in a way that feels organic and enticing to [targetAudience].

  The script must:
  Not begin with "Hey" or any rhetorical question.
  Avoid any nicknames (e.g., babe, fam, hun, etc.).
  Include a friendly opening that quickly captures attention without sounding formal or corporate.
  Clearly mention [promoDetails].
  Showcase [productTitle] (if provided) naturally, tying it into how it benefits or appeals to [targetAudience].
  Maintain a modern, conversational tone—upbeat but not over-the-top.
  End with a concise, inviting call-to-action to redeem the promo.
  Fit within 15 seconds total.
  `,
  [ScriptTemplates.productsComparison]: `
  You’re an influencer creating a short, 15-second TikTok or Instagram video comparing two products from the same brand.
  You love both products [productTitle1] and [productTitle2]—and the brand itself.
  Mention descripton from [productDescription1] and [productDescription2] if provided, highlight their differences, and keep it conversational yet high-energy.
  Avoid using greetings like ‘besties’ or ‘babes,’ don’t include hashtags or emojis, and don’t address the audience directly by name.
  Make it clear you’re not an employee—just an excited collaborator or fan.
  Stay under 15 seconds in total, and focus on why each product is special in its own way.
  End with an enthusiastic note about the brand’s quality or style, keeping the whole script approachable and trendy.
  `,
  [ScriptTemplates.productRelease]: `
  Generate a 15-second viral product launch video script for [productTitle] by [brandName].
  The script should begin with an engaging hook to capture attention within the first three seconds.
  It should then highlight the product’s key features and benefits in a clear, energetic, and compelling way.
  The ending should include a strong call to action that drives urgency and engagement.
  The tone should be trendy, persuasive, and tailored for social media platforms like TikTok, Instagram Reels, and YouTube Shorts.
  Do not use emojis. Ensure the script is concise, fast-paced, and optimized for high engagement.
  Do not break the script into seconds; it needs to be one paragraph."
  `,
  [ScriptTemplates.brandReview]: `
  Act as a loyal fan of [brandName].
  Create a 15-second video testimonial that begins without using any greeting like “besties,” “babes,” or similar.
  Use a personal, conversational, and trendy tone.
  Mention a specific pain point you had before discovering [brandName], then explain how the brand resolved it and why it stands out from competitors.
  Keep it short, energetic, and engaging so it can go viral on social media while remaining professional enough for the official brand website. Do not include any hashtags or tags.
  Do not wrap the script in quotation marks. Provide the final script only.
  `,
};

export const SCRIPT_TEMPLATE_SETTINGS: Record<ScriptTemplates, { title: string; prompt: string }> = {
  [ScriptTemplates.hypeCheck]: {
    title: 'Hype check: Feature Showcase',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.hypeCheck],
  },
  [ScriptTemplates.justUnboxed]: {
    title: 'Just Unboxed: My New Favorite',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.justUnboxed],
  },
  [ScriptTemplates.brandLoyalist]: {
    title: `Brand Loyalist: Why I'm Hooked`,
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.brandLoyalist],
  },
  [ScriptTemplates.teamTalk]: {
    title: 'Team Talk: Product Highlights',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.teamTalk],
  },
  [ScriptTemplates.behindTheDesign]: {
    title: 'Behind the Design: Our Innovation Story',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.behindTheDesign],
  },
  [ScriptTemplates.promotion]: {
    title: 'Product Promotion: Save Big',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.promotion],
  },
  [ScriptTemplates.productsComparison]: {
    title: 'Product Comparison: Why I Love Both',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.productsComparison],
  },
  [ScriptTemplates.productRelease]: {
    title: 'Product Release: Meet the New',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.productRelease],
  },
  [ScriptTemplates.brandReview]: {
    title: 'Brand Review: Why I Love It',
    prompt: SCRIPT_TEMPLATE_PROMPTS[ScriptTemplates.brandReview],
  },
};

export const getFollowupScriptGuidance = (reference: string) => `
  You're given the following video review script: 

  "${reference}"

  Your mission: Transform this script into a viral hit!

  Guidance:
    - Stay true to the core: Keep the original message and feel intact.
    - Keep it tight: Add impact, but don't lose focus. Every word should serve a purpose. 
`;

export const DEFAULT_SCRIPT_GUIDANCE = `
  Additional guidance:  
    - Product name should be mentioned in the video. Keep the name simple, don't include numbers unless they are essential to identify the product.  
    - Make it as conversational as possible. 
    - Only mention product features that are explicitly mentioned, don’t make things up. 
    - Don’t wrap the script with quotation marks. don't include hashtags, tags and emojis  
    - THE SCRIPT SHOULD BE EXACTLY 3 SENTENCES LONG! Do not write more than three sentences! Try to keep it short, and in any case, do not write more than 60 words. 
    - DON’T EXPLICITLY MENTION THE TARGET AUDIENCE!. Only use it to guide the tone and style of the script!
    - Don't start the video with the words 'besties', 'babes' or any similar word. 
    - Always replace placeholder wrapped with [] with the actual values. If you can't find the actual value rephrase the sentence but don't include the placeholder name as is. 
`;

export const FOUNDER_VIDEO_SCRIPT_GUIDANCE = `
  Additional guidance:  
    - Brand name should be mentioned in the video.
    - Don't focuse on a single product, focus on the brand as a whole.
    - Make it as conversational as possible. 
    - Don’t wrap the script with quotation marks. don't include hashtags, tags and emojis  
    - THE SCRIPT SHOULD BE EXACTLY 3 SENTENCES LONG! Do not write more than three sentences! Try to keep it short, and in any case, do not write more than 60 words. 
    - DON’T EXPLICITLY MENTION THE TARGET AUDIENCE!. Only use it to guide the tone and style of the script!
    - Don't start the video with the words 'besties', 'babes' or any similar word. 
`;
